import { z } from 'zod';
import { brandCodeSchema, LANGUAGE_SOURCES, TIMEZONE_SOURCES } from './enums.js';
import { normalizeEvent } from './compat.js';
/**
 * Schemata prichozich udalosti (SPEC kap. 4.1, priklady v referencnim
 * dokumentu kap. 2.5). Snimky, ne rozdily: kazda udalost nese cely aktualni
 * stav objektu s rostouci verzi.
 */
/** Datum nebo datum s casem. Datum bez casu se vyklada jako 00:00 v pasmu zavodu. */
export const dateish = z
    .string()
    .min(8)
    .refine((v) => !Number.isNaN(Date.parse(v.length === 10 ? `${v}T00:00:00Z` : v)), {
    message: 'Neplatne datum',
});
export const dateStatusValues = ['unknown', 'provisional', 'confirmed', 'cancelled'];
export const salesStatusValues = ['not_started', 'open', 'limited', 'sold_out', 'closed'];
export const itemTypeValues = [
    'ticket',
    'package',
    'guide',
    'giftcard',
    'flexi',
    'parking',
    'other',
];
export const ticketStatusValues = [
    'pending',
    'allocated',
    'issued',
    'dispatched',
    'delivered',
];
export const flexiStatusValues = [
    'none',
    'active',
    'claim_in_progress',
    'used',
    'expired',
];
export const giftStatusValues = [
    'purchased',
    'delivered',
    'redeemed',
    'expired',
    'cancelled',
];
export const availabilityValues = [
    'in_stock',
    'on_demand',
    'limited',
    'sold_out',
    'not_on_sale',
];
/** Stav tribuny, jak ho posílá e-shop (`availability.updated`, tvar „jedna tribuna“). */
export const shopAvailabilityValues = ['not_on_sale', 'available', 'sold_out'];
export const consentStateValues = ['granted', 'not_given', 'withdrawn'];
export const consentPurposeValues = ['marketing', 'watchdog'];
export const watchdogTypeValues = ['date', 'availability', 'next_season', 'sale_start'];
/** `active` je název e-shopu pro `confirmed`; `matched` a `expired` jsou stavy hubu. */
export const watchdogStatusValues = [
    'pending',
    'confirmed',
    'active',
    'matched',
    'cancelled',
    'expired',
];
/** Prazdny retezec se nikdy neposila; kdyz ho prece prijde, bereme ho jako chybejici. */
const optionalText = z
    .string()
    .trim()
    .min(1)
    .optional()
    .or(z.literal('').transform(() => undefined));
export const contactPartSchema = z.object({
    email: z.string().email(),
    language: optionalText,
    country: optionalText,
    phone: optionalText,
    timezone: optionalText,
});
export const orderItemSchema = z.object({
    item_id: z.string().min(1),
    type: z.enum(itemTypeValues),
    race_id: optionalText,
    category: optionalText,
    /** ID vstupenky, u jiných druhů ID prodaného produktu (X54). */
    category_id: optionalText,
    qty: z.number().int().positive().optional(),
    price: z.number().optional(),
    certificate_id: optionalText,
    ticket_status: z.enum(ticketStatusValues).optional(),
    active: z.boolean().optional(),
    /** Vráceno přes Flexi (H31): nese totéž co `active: false`, hub ho jen přijme. */
    flexi_returned: z.boolean().optional(),
    flexi: z
        .object({
        covers: z.array(z.string()).optional(),
        buy_until: dateish.optional(),
        use_until: dateish.optional(),
        status: z.enum(flexiStatusValues).optional(),
        purchased_at: dateish.optional(),
    })
        .optional(),
    gift: z
        .object({
        gift_id: z.string().min(1),
        recipient_email: optionalText,
        recipient_name: optionalText,
        deliver_at: dateish.optional(),
        expires_at: dateish.optional(),
        status: z.enum(giftStatusValues).optional(),
        redeemed_order_code: optionalText,
    })
        .optional(),
});
export const orderUpdatedSchema = z.object({
    order_code: z.string().min(1),
    status: z.string().min(1),
    seller: brandCodeSchema,
    customer_id: z.string().min(1),
    contact: contactPartSchema.optional(),
    currency: optionalText,
    total: z.number().optional(),
    frontend: optionalText,
    /** Závod objednávky (D-179); u storna s prázdnými položkami jediná vazba na závod. */
    race_id: optionalText,
    created_at: dateish.optional(),
    paid_at: dateish.optional(),
    /** Token hlídacího psa, ze kterého zákazník přišel (e-shop ho drží v relaci). */
    watchdog_token: optionalText,
    items: z.array(orderItemSchema).default([]),
});
/**
 * Snimek zavodu je castecny: e-shop vlastni jen pole podle `field_owner`
 * (SPEC kap. 3). Pole, ktera ve snimku nejsou, se nemeni (DECISIONS D-26).
 */
export const raceUpdatedSchema = z.object({
    race_id: z.string().min(1),
    series: optionalText,
    event: optionalText,
    season: z.number().int().optional(),
    name: optionalText,
    circuit: optionalText,
    country: optionalText,
    timezone: optionalText,
    date_status: z.enum(dateStatusValues).optional(),
    date_start: dateish.nullish(),
    date_end: dateish.nullish(),
    sales_status: z.enum(salesStatusValues).optional(),
    flexi_buy_until: dateish.nullish(),
    flexi_use_until: dateish.nullish(),
    next_race_id: optionalText,
    expected_ticket_release: dateish.nullish(),
    expected_start: dateish.nullish(),
    promise_by: dateish.nullish(),
    race_pack_ready: z.boolean().optional(),
    race_pack_url: optionalText,
    review_policy: z.enum(['standard', 'none']).optional(),
    /** Firma, která závod prodává dnes (D-179, H12). */
    brand: brandCodeSchema.optional(),
    /** ID ročníku v e-shopu (číslo za poslední pomlčkou `race_id`). */
    shop_race_id: optionalText,
    /** Plánovaný začátek prodeje, jen datum (H37): hub ho přijme, prodej řídí dostupnost vstupenek. */
    sales_start_date: dateish.nullish(),
});
export const contactUpdatedSchema = z.object({
    customer_id: z.string().min(1),
    email: z.string().email(),
    language: optionalText,
    country: optionalText,
    phone: optionalText,
    timezone: optionalText,
    merged_into: optionalText,
    first_name: optionalText,
    created_at: dateish.optional(),
    language_source: z.enum(LANGUAGE_SOURCES).optional(),
    timezone_source: z.enum(TIMEZONE_SOURCES).optional(),
    brand: brandCodeSchema.optional(),
});
export const consentChangedSchema = z
    .object({
    /** Souhlas z newsletteru nemá zákaznický účet; stačí e-mail (D-178). */
    customer_id: optionalText,
    email: z.string().email().optional(),
    controller: brandCodeSchema,
    purpose: z.enum(consentPurposeValues),
    state: z.enum(consentStateValues),
    /** Jiné médium než e-mail hub odmítne, aby se souhlas se SMS nezapsal jako e-mailový. */
    medium: z.literal('email').optional(),
    source: optionalText,
    text_version: optionalText,
    changed_at: dateish.optional(),
})
    .superRefine((d, ctx) => {
    if (!d.customer_id && !d.email) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['email'],
            message: 'Chybí `customer_id` nebo `email`.',
        });
    }
});
export const giftUpdatedSchema = z.object({
    gift_id: z.string().min(1),
    order_code: optionalText,
    item_id: optionalText,
    buyer_customer_id: optionalText,
    recipient_email: optionalText,
    recipient_name: optionalText,
    message: optionalText,
    deliver_at: dateish.nullish(),
    expires_at: dateish.nullish(),
    status: z.enum(giftStatusValues),
    redeemed_order_code: optionalText,
    recipient_customer_id: optionalText,
    event: optionalText,
    value: z.number().optional(),
    currency: optionalText,
    name: optionalText,
});
/** Token hlídacího psa vymyslel e-shop; smí být v URL bez kódování. */
const watchdogTokenSchema = z
    .string()
    .regex(/^[A-Za-z0-9._~-]{16,200}$/, 'Token: 16 až 200 znaků A-Z a-z 0-9 . _ ~ -');
export const watchdogUpdatedSchema = z
    .object({
    watchdog_id: z.string().min(1),
    customer_id: optionalText,
    email: z.string().email(),
    /** Správce (značka). E-shop posílá `seller`; `controller` je starší název téhož. */
    controller: brandCodeSchema.optional(),
    seller: brandCodeSchema.optional(),
    type: z.enum(watchdogTypeValues),
    race_id: optionalText,
    event: optionalText,
    category_id: optionalText,
    /** `null` = celý závod (všechny tribuny). */
    category: optionalText.nullable(),
    status: z.enum(watchdogStatusValues),
    token: watchdogTokenSchema.optional(),
    language: optionalText,
    first_name: optionalText,
    quantity: z.number().int().positive().max(100).optional(),
    created_at: dateish.optional(),
    confirmed_at: dateish.nullish(),
    submission_id: optionalText,
    /** Odkazy z e-shopu (H39); hub je použije jen z https na doméně značky. */
    confirm_url: optionalText,
    cancel_url: optionalText,
    cancel_all_url: optionalText,
    consent: z
        .object({
        text_version: optionalText,
        ip: optionalText,
        user_agent: optionalText,
        source_url: optionalText,
    })
        .optional(),
    source: z
        .object({
        url: optionalText,
        utm_source: optionalText,
        utm_medium: optionalText,
        utm_campaign: optionalText,
    })
        .optional(),
})
    .superRefine((d, ctx) => {
    if (!d.controller && !d.seller) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['seller'],
            message: 'Chybí `seller` (nebo starší `controller`).',
        });
    }
})
    .transform((d) => ({
    ...d,
    // hub dál pracuje jen se `controller` a se stavem `confirmed`
    controller: (d.controller ?? d.seller),
    status: (d.status === 'active' ? 'confirmed' : d.status),
}));
export const supportContactSchema = z.object({
    customer_id: optionalText,
    email: z.string().email().optional(),
    order_code: optionalText,
    topic: optionalText,
    at: dateish.optional(),
    task_id: optionalText,
    brand: brandCodeSchema.optional(),
    language: optionalText,
    category: optionalText,
});
/** Odpověď podpory zákazníkovi (e-shop, D-182). Text a předmět se nikdy neukládají. */
export const supportReplySchema = z.object({
    task_id: z.string().min(1),
    email: z.string().email(),
    brand: brandCodeSchema.optional(),
    language: optionalText,
    sent_at: dateish.optional(),
});
/** E-shop smazal zákazníka (GDPR): hub provede stejný výmaz jako akce v administraci (D-177). */
export const contactErasedSchema = z
    .object({ customer_id: optionalText, email: z.string().email().optional() })
    .superRefine((d, ctx) => {
    if (!d.customer_id && !d.email) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['customer_id'],
            message: 'Chybí `customer_id` nebo `email`.',
        });
    }
});
export const SUPPORT_CASE_CHANNELS = ['email', 'phone', 'chat', 'social', 'shop', 'other'];
/** Případ z e-shopu nebo helpdesku: stav, výsledek a spokojenost (D-107). Aktualizuje se podle `case_id`. */
export const supportCaseSchema = z.object({
    case_id: z.string().min(1).max(200),
    customer_id: optionalText,
    email: z.string().email().optional(),
    seller: brandCodeSchema.optional(),
    race_id: optionalText,
    order_code: optionalText,
    channel: z.enum(SUPPORT_CASE_CHANNELS).default('shop'),
    topic: optionalText,
    status: z.enum(['open', 'resolved']),
    resolution: optionalText,
    satisfaction: z.enum(['satisfied', 'neutral', 'dissatisfied', 'unknown']).default('unknown'),
    opened_at: dateish.optional(),
    closed_at: dateish.nullish(),
});
/** E-shop oznamuje, ze sam odeslal transakcni e-mail. Hub ho jen loguje. */
export const messageSentSchema = z.object({
    customer_id: optionalText,
    email: z.string().email().optional(),
    code: optionalText,
    template_key: z.string().min(1),
    provider_message_id: optionalText,
    seller: brandCodeSchema.optional(),
    language: optionalText,
    order_code: optionalText,
    race_id: optionalText,
    sent_at: dateish.optional(),
    /** Odkud zpráva vznikla, např. `support-task-4521`, `order-OF61260088`. */
    source: optionalText,
});
const availabilityCategorySchema = z.object({
    category: z.string().min(1),
    category_id: optionalText,
    name: optionalText,
    price: z.number().optional(),
    currency: optionalText,
    url: optionalText,
    availability: z.enum(availabilityValues),
    qty: z.number().int().optional(),
});
/**
 * Dostupnost se posílá buď po tribunách (`category` + `status`, tvar e-shopu),
 * nebo jako seznam `categories` (starší tvar). Hub je při zpracování sjednotí.
 */
export const availabilityUpdatedSchema = z
    .object({
    /** Ročník; chybí-li, určí se z `event` (aktuální ročník, D-179). */
    race_id: optionalText,
    event: optionalText,
    categories: z.array(availabilityCategorySchema).default([]),
    category: optionalText,
    category_id: optionalText,
    status: z.enum(shopAvailabilityValues).optional(),
    name: optionalText,
    price: z.number().optional(),
    currency: optionalText,
    url: optionalText,
    qty: z.number().int().optional(),
})
    .superRefine((d, ctx) => {
    if (!d.race_id && !d.event) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['event'],
            message: 'Chybí `race_id` nebo `event`.',
        });
    }
    if (d.category && !d.status) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['status'],
            message: 'U `category` je `status` povinný.',
        });
    }
});
const slugs = z.array(z.string().min(1).max(100)).max(50).default([]);
export const contentPublishedSchema = z.object({
    brand: brandCodeSchema,
    content_id: z.string().min(1).max(200),
    url: z.string().url(),
    canonical_url: optionalText,
    title: z.string().trim().min(1),
    summary: optionalText,
    language: optionalText,
    translations: z
        .array(z.object({
        language: z.string().min(2),
        content_id: z.string().min(1),
        url: z.string().url(),
    }))
        .default([]),
    published_at: dateish.optional(),
    updated_at: dateish.optional(),
    image_url: optionalText,
    image_alt: optionalText,
    author: optionalText,
    series: optionalText,
    race_ids: slugs,
    topics: slugs,
    people: slugs,
    teams: slugs,
    premium: z.boolean().default(false),
    status: z.enum(['published', 'unpublished']).default('published'),
});
/** Události aplikace (příprava, návrh značek kap. 7): jen se přijmou a uloží. */
export const appSessionSchema = z.object({
    brand: brandCodeSchema,
    customer_id: z.string().min(1),
    device_id: z.string().min(1).max(200),
    platform: z.enum(['ios', 'android']).optional(),
    app_version: optionalText,
    language: optionalText,
    tz: optionalText,
    at: dateish.optional(),
});
export const appPushTokenSchema = z.object({
    brand: brandCodeSchema,
    customer_id: z.string().min(1),
    device_id: z.string().min(1).max(200),
    platform: z.enum(['ios', 'android']).optional(),
    /** Chybí-li, token se smaže (uživatel push vypnul). */
    token: z.string().min(1).max(4096).optional(),
    enabled: z.boolean(),
    at: dateish.optional(),
});
export const eventDataSchemas = {
    'order.updated': orderUpdatedSchema,
    'race.updated': raceUpdatedSchema,
    'contact.updated': contactUpdatedSchema,
    'consent.changed': consentChangedSchema,
    'gift.updated': giftUpdatedSchema,
    'watchdog.updated': watchdogUpdatedSchema,
    'support.contact': supportContactSchema,
    'support.reply': supportReplySchema,
    'contact.erased': contactErasedSchema,
    'support.case': supportCaseSchema,
    'message.sent': messageSentSchema,
    'availability.updated': availabilityUpdatedSchema,
    'content.published': contentPublishedSchema,
    'app.session': appSessionSchema,
    'app.push_token': appPushTokenSchema,
};
export const EVENT_TYPES = Object.keys(eventDataSchemas);
/** Typy udalosti, ktere nejsou snimkem agregatu — nemaji verzi (DECISIONS D-27). */
export const VERSIONLESS_EVENT_TYPES = [
    'consent.changed',
    'support.contact',
    'support.reply',
    'contact.erased',
    'support.case',
    'message.sent',
    'app.session',
    'app.push_token',
];
export const envelopeSchema = z.object({
    event_id: z.string().min(1).max(200),
    schema_version: z.number().int().positive().default(1),
    type: z.enum(EVENT_TYPES),
    aggregate_id: z.string().min(1),
    version: z.number().int().nonnegative().optional(),
    occurred_at: dateish,
    /** Historický import (D-174): událost nikdy nespustí odeslání. */
    historical: z.boolean().optional(),
    data: z.unknown(),
});
export const eventBatchSchema = z.union([envelopeSchema, z.array(envelopeSchema).min(1).max(500)]);
/** Overi obalku i data podle typu. */
export function parseEvent(raw) {
    const env = envelopeSchema.safeParse(raw);
    if (!env.success) {
        return {
            ok: false,
            error: env.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`).join('; '),
        };
    }
    const schema = eventDataSchemas[env.data.type];
    const normalized = normalizeEvent(env.data.type, env.data.data, {
        aggregateId: env.data.aggregate_id,
        occurredAt: env.data.occurred_at,
        historical: env.data.historical,
    });
    const data = schema.safeParse(normalized);
    if (!data.success) {
        return {
            ok: false,
            error: data.error.issues.map((i) => `data.${i.path.join('.')}: ${i.message}`).join('; '),
        };
    }
    return { ok: true, envelope: env.data, data: data.data };
}
//# sourceMappingURL=events.js.map